import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import AddIcon from '@material-ui/icons/Add';
import compose from 'helpers/compose';
import NewAccountInstructionsDialog from './NewAccountInstructionsDialog';
import Info from 'components/Tooltip/Info';
import VariantSelect from 'components/VariantSelect/VariantSelect';

import { SingleValueResponse } from 'common/interfaces';
import { AccountsStore } from 'stores/accountsStore';
import { ApiStore } from 'stores/apiStore';
import { ACCOUNTS_EDIT } from 'routes';
import { API_ACCOUNT } from 'api/endpoints';
import { AccountStatus, PersonType, Currency } from 'enums';
import { UserStoreKC } from 'stores/userStoreKC';
import { EditAccountStore } from 'stores/editAccountStore';
import { Permission } from 'helpers/permissions';

import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';

interface Props {
  apiStore?: ApiStore;
  accountsStore?: AccountsStore;
  userStore?: UserStoreKC;
  editAccountStore: EditAccountStore;
}

const NewAccountButton: React.FC<Props> = (props) => {
  const { userStore, editAccountStore } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [mockAccount, setMockAccount] = useState(false);
  const [personTypeDialog, setPersonTypeDialog] = useState(false);
  const [currency, setCurrency] = useState(Currency.CZK); // EUR

  const allowNewAccount = userStore!.checkPermissions(Permission.ACCOUNT_CREATE_NEW);
  const allowNewMockAccount = userStore!.checkPermissions(Permission.ACCOUNT_CREATE_MOCK);
  const allowPhysical = userStore!.checkPermissions(Permission.ACCOUNT_CREATE_PHYSICAL);
  const allowLegal = userStore!.checkPermissions(Permission.ACCOUNT_CREATE_LEGAL);
  const allowCurrencyEur = userStore!.checkPermissions(Permission.ACCOUNT_CURRENCY_EUR);

  const openNewAccount = async (personType: PersonType, currency: Currency, isMock?: boolean) => {
    const { post } = props.apiStore as ApiStore;
    const response = await post<SingleValueResponse<string>>(API_ACCOUNT, {
      personType,
      isMock,
      currency,
    });
    if (response?.data) {
      history.push(ACCOUNTS_EDIT(response.data.value));
    }
  };

  const handleNewAccountClick = async () => {
    if (allowNewAccount) {
      if (allowCurrencyEur) {
        setPersonTypeDialog(true);
      } else {
        allowPhysical && !allowLegal && openNewAccount(PersonType.PHYSICAL, Currency.CZK, false);
        !allowPhysical && allowLegal && openNewAccount(PersonType.LEGAL, Currency.CZK, false);
        allowPhysical && allowLegal && setPersonTypeDialog(true);
      }
    } else {
      setIsInstructionsOpen(true);
    }
  };

  const handleNewMockAccountClick = async () => {
    setMockAccount(true);
    editAccountStore.accountStatus = AccountStatus.MOCK;
    setPersonTypeDialog(true);
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        className="secondaryGreen new-account-button"
        id="new-account-button"
        onClick={handleNewAccountClick}
        startIcon={<AddIcon fontSize="medium" />}
      >
        {t('general_newAccount')}
      </Button>

      {allowNewMockAccount && (
        <Button
          variant="contained"
          color="secondary"
          className="new-account-button"
          id="new-mock-account-button"
          onClick={handleNewMockAccountClick}
        >
          {t('general_newMockAccount')}
        </Button>
      )}

      <NewAccountInstructionsDialog open={isInstructionsOpen} close={() => setIsInstructionsOpen(false)} />

      <Dialog open={personTypeDialog} maxWidth="sm">
        <DialogHeader onClose={() => setPersonTypeDialog(false)}>{t('newAccount_title')}</DialogHeader>
        <DialogContent>
          {allowCurrencyEur && (
            <>
              <Typography variant={'body2'}>
                {t('newAccount_currency')} <Info content={t('newAccount_currency_tooltip')} heading />
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup name="access" onChange={(e) => setCurrency(e.target.value as Currency)} value={currency}>
                  <div>
                    <FormControlLabel value={'CZK'} control={<Radio />} label={t(`CZK`)} />
                  </div>
                  <div>
                    <FormControlLabel value={'EUR'} control={<Radio />} label={t(`EUR`)} />
                  </div>
                </RadioGroup>
              </FormControl>
            </>
          )}

          <VariantSelect
            dialog
            title="newAccount_info"
            variants={[
              {
                title: 'general_person_PHYSICAL',
                action: () => openNewAccount(PersonType.PHYSICAL, currency, mockAccount),
                hidden: !allowPhysical,
              },
              {
                title: 'general_person_LEGAL',
                action: () => openNewAccount(PersonType.LEGAL, currency, mockAccount),
                hidden: !allowLegal,
              },
            ]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default compose(inject('apiStore', 'userStore', 'editAccountStore'), observer)(NewAccountButton);
